import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Toast, { type ToastType } from '@/components/feature/Toast';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { getMovieById, getRelatedMovies, formatHeat } from '@/utils/movie';
import PlayerArea from './components/PlayerArea';
import StillsGallery from './components/StillsGallery';
import ShareDialog from './components/ShareDialog';
import RelatedMovies from './components/RelatedMovies';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id ?? '');
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType }>({ message: '', type: 'info' });
  const [toastVisible, setToastVisible] = useState(false);

  const related = useMemo(() => (movie ? getRelatedMovies(movie) : []), [movie]);

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type });
    setToastVisible(true);
  };

  useDocumentMeta({
    title: movie
      ? `${movie.title}高清完整版在线观看 - 善良的小姨子`
      : '影片未找到 - 善良的小姨子影视站',
    description: movie
      ? `${movie.title}（${movie.year}）由${movie.director}执导，${movie.cast.join('、')}主演。${movie.summary} 善良的小姨子影视站提供剧情简介与在线观看入口。`
      : '未找到对应影片，请返回首页浏览更多内容。',
    keywords: movie ? `${movie.title},${movie.title}在线观看,${movie.types.join(',')},善良的小姨子影视站` : '影片未找到',
  });

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center min-h-[60vh]">
          <span className="w-16 h-16 rounded-full bg-background-800 flex items-center justify-center text-foreground-400">
            <i className="ri-film-line text-3xl"></i>
          </span>
          <h1 className="mt-5 text-2xl font-heading font-black text-foreground-50">影片未找到</h1>
          <p className="mt-2 text-sm text-foreground-400">该影片可能已下架或链接有误，返回首页看看其他内容吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const favorited = isFavorite(movie.id);

  return (
    <SiteLayout>
      {/* Header band */}
      <section className="relative w-full overflow-hidden border-b border-background-800">
        <img src={movie.backdrop} alt={`${movie.title} 背景`} className="absolute inset-0 w-full h-full object-cover object-top opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/85 to-background-950/70"></div>
        <div className="relative w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 md:pb-12">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/movie-category" className="hover:text-primary-400 transition-colors cursor-pointer">电影分类</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-200">{movie.title}</span>
          </nav>

          <div className="flex items-center gap-3 md:gap-4">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-background-800/70 border border-background-700 text-sm text-foreground-200 hover:bg-background-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回上一页
            </button>
            <Link
              to="/movie-category"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-background-800/70 border border-background-700 text-sm text-foreground-200 hover:bg-background-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-grid-line"></i>
              分类页
            </Link>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-950">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* Left: poster + player */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <PlayerArea movie={movie} />

            <div className="mt-8 md:mt-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-6 rounded-full bg-primary-500"></span>
                <h2 className="text-lg md:text-xl font-heading font-black text-foreground-50">剧情简介</h2>
              </div>
              <p className="text-sm md:text-base text-foreground-200 leading-loose">{movie.synopsis}</p>
            </div>

            <div className="mt-8 md:mt-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                <h2 className="text-lg md:text-xl font-heading font-black text-foreground-50">精彩片段 · 剧照</h2>
                <span className="text-xs text-foreground-400">点击可放大预览</span>
              </div>
              <StillsGallery movie={movie} />
            </div>
          </div>

          {/* Right: info */}
          <aside className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-24 flex flex-col gap-5">
              <div className="rounded-lg overflow-hidden border border-background-800 bg-background-900">
                <div className="w-full aspect-[2/3] bg-background-800">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 电影海报`}
                    title={`${movie.title} 在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {movie.types.map((type) => (
                    <Link
                      key={type}
                      to={`/movie-category?type=${encodeURIComponent(type)}`}
                      className="px-2.5 py-1 rounded-full text-xs bg-secondary-500/15 text-secondary-300 hover:bg-secondary-500/25 transition-colors cursor-pointer"
                    >
                      {type}
                    </Link>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-black text-foreground-50 leading-tight">
                  {movie.title}
                </h1>
                <div className="mt-3 flex items-center gap-4 flex-wrap text-sm text-foreground-300">
                  <span className="flex items-center gap-1 text-primary-400 font-semibold">
                    <i className="ri-star-fill"></i>
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-accent-400"></i>
                    热度 {formatHeat(movie.heat)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {movie.year}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-background-900 border border-background-800 p-4 md:p-5">
                <h3 className="text-sm font-semibold text-foreground-100 mb-4">影片资料</h3>
                <dl className="flex flex-col gap-3 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-foreground-400 whitespace-nowrap">导演</dt>
                    <dd className="text-foreground-100 text-right">{movie.director}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-foreground-400 whitespace-nowrap">主演</dt>
                    <dd className="text-foreground-100 text-right">{movie.cast.join('、')}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-foreground-400 whitespace-nowrap">类型</dt>
                    <dd className="text-foreground-100 text-right">{movie.types.join(' · ')}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-foreground-400 whitespace-nowrap">地区</dt>
                    <dd className="text-foreground-100 text-right">{movie.region}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-foreground-400 whitespace-nowrap">片长</dt>
                    <dd className="text-foreground-100 text-right">{movie.duration}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-foreground-400 whitespace-nowrap">上映</dt>
                    <dd className="text-foreground-100 text-right">{movie.year} 年</dd>
                  </div>
                </dl>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const added = toggleFavorite(movie.id);
                    showToast(added ? '已加入收藏（本机）' : '已取消收藏', added ? 'success' : 'info');
                  }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-800 border border-background-700 text-foreground-100 hover:bg-background-700'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-background-800 border border-background-700 text-foreground-100 text-sm font-semibold hover:bg-background-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>

              <p className="text-xs text-foreground-500 leading-relaxed flex items-start gap-2">
                <i className="ri-information-line mt-0.5"></i>
                收藏记录保存在本机浏览器，换设备后不会同步。
              </p>
            </div>
          </aside>
        </div>
      </section>

      <RelatedMovies movies={related} />

      <ShareDialog
        movie={movie}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onCopied={() => {
          setShareOpen(false);
          showToast('链接已复制到剪贴板', 'success');
        }}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        visible={toastVisible}
        onClose={() => setToastVisible(false)}
      />
    </SiteLayout>
  );
}